import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Card, Button, Alert, Form, Badge } from 'react-bootstrap';
import { FaStar, FaArrowLeft, FaArrowRight, FaPaperPlane, FaCheck, FaChalkboardTeacher, FaSearch, FaCalendarAlt } from 'react-icons/fa';
import Navbar from '../../dashboard/navbar';
import Sidebar from '../../dashboard/sidebar';
import useAuth from '../../dashboard/useAuth';
import axios from 'axios';
import Select from 'react-select';

const FormulaireAvisClient = ({ titre }) => {
    useAuth();

    // États
    const [step, setStep] = useState(1); // 1: Sélection formation, 2: Réponse questions
    const [formations, setFormations] = useState([]);
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [alert, setAlert] = useState({ message: '', type: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [loadingFormations, setLoadingFormations] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedDates, setExpandedDates] = useState({});

    const user_id = localStorage.getItem('user_id');
    const location = useLocation();
    const navigate = useNavigate();
    const { etapeId } = location.state || {};

    // Effets
    useEffect(() => {
        if (user_id) {
            document.title = "eTrainApp | Avis Client";
            fetchFormations();
        }
    }, [user_id]);

    // Récupérer les formations
    const fetchFormations = async () => {
        setLoadingFormations(true);
        try {
            const response = await axios.get(process.env.REACT_APP_HOTE + '/liste-formation/');
            if (response.data) {
                // Filtrer les formations actives si nécessaire
                const formationsActives = response.data.filter(f => f.statut === 'actif' || !f.statut);
                setFormations(formationsActives);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des formations:', error);
            showAlert('Erreur lors du chargement des formations', 'danger');
        } finally {
            setLoadingFormations(false);
            setIsLoading(false);
        }
    };

    // Fonctions pour la gestion des dates (comme dans ListeSouscriptions)
    const isValidDate = (dateString) => {
        if (!dateString) return false;
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date);
    };

    const normalizeDate = (dateString) => {
        if (!dateString) return null;

        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('/');
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }

        const date = new Date(dateString);
        if (isValidDate(date)) {
            return date.toISOString().split('T')[0];
        }

        return null;
    };

    const isDateList = (periode) => {
        if (!periode) return false;

        if (typeof periode === 'string') {
            try {
                const cleanedPeriode = periode.replace(/'/g, '"');
                const parsed = JSON.parse(cleanedPeriode);
                return Array.isArray(parsed) && parsed.length > 0;
            } catch (error) {
                return false;
            }
        }

        return Array.isArray(periode) && periode.length > 0;
    };

    const formatFormationPeriod = (periode) => {
        if (!periode || periode === '[]' || periode === '""' || periode === "''") {
            return "À la demande";
        }

        if (isDateList(periode)) {
            try {
                let dates;
                if (typeof periode === 'string') {
                    const cleanedPeriode = periode.replace(/'/g, '"');
                    dates = JSON.parse(cleanedPeriode);
                } else {
                    dates = periode;
                }

                const validDates = dates
                    .map(date => normalizeDate(date))
                    .filter(date => date !== null && isValidDate(new Date(date)))
                    .sort();

                if (validDates.length > 0) {
                    // Retourner une chaîne de caractères au lieu de JSX
                    return validDates.map((date, index) => {
                        const dateObj = new Date(date);
                        return dateObj.toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit'
                        });
                    }).join(', ');
                }
            } catch (error) {
                console.warn('Erreur de parsing des dates:', error);
            }
        }

        if (typeof periode === 'string' && periode.trim() !== '') {
            return periode;
        }

        return '-';
    };

    // Fonction pour obtenir le prix de la formation (comme dans ListeSouscriptions)
    const getPrixFormation = (formationItem) => {
        return formationItem.prix_particulier > 0 && formationItem.prix_particulier
            ? formationItem.prix_particulier
            : formationItem.prix;
    };

    // Fonction pour afficher toutes les dates (comme dans ListeSouscriptions)
    const getAllDatesDisplay = (periode, formationId) => {
        // Vérifier si periode est vide, null, ou un tableau vide
        if (!periode || periode === '[]' || periode === '""' || periode === "''") {
            return 'À la demande';
        }

        if (isDateList(periode)) {
            try {
                let dates;
                if (typeof periode === 'string') {
                    // Nettoyer la chaîne - remplacer les guillemets simples par des doubles
                    const cleanedPeriode = periode.replace(/'/g, '"');
                    dates = JSON.parse(cleanedPeriode);
                } else {
                    dates = periode;
                }

                // Vérifier si le tableau est vide après parsing
                if (!Array.isArray(dates) || dates.length === 0) {
                    return 'À la demande';
                }

                const validDates = dates
                    .map(date => normalizeDate(date))
                    .filter(date => date !== null && isValidDate(new Date(date)))
                    .sort();

                if (validDates.length > 0) {
                    const isExpanded = expandedDates[formationId];
                    const displayDates = isExpanded ? validDates : validDates.slice(0, 3);
                    const remainingCount = validDates.length - 3;

                    const toggleDates = (e) => {
                        e.stopPropagation(); // Empêcher la sélection de la ligne
                        setExpandedDates(prev => ({
                            ...prev,
                            [formationId]: !prev[formationId]
                        }));
                    };

                    const textColorClass = 'text-muted';
                    const linkColorClass = 'text-primary';

                    return (
                        <div className="small" style={{
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.4'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {displayDates.map((date, index) => {
                                    const dateObj = new Date(date);
                                    return (
                                        <span
                                            key={index}
                                            className={textColorClass}
                                            style={{
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0
                                            }}
                                        >
                                            {dateObj.toLocaleDateString('fr-FR', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: '2-digit'
                                            })}
                                            {index < displayDates.length - 1 && ','}
                                        </span>
                                    );
                                })}
                                {!isExpanded && remainingCount > 0 && (
                                    <span
                                        className={`${linkColorClass} ms-1`}
                                        style={{
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap'
                                        }}
                                        onClick={toggleDates}
                                        title="Cliquer pour voir toutes les dates"
                                    >
                                        ...(+{remainingCount})
                                    </span>
                                )}
                                {isExpanded && remainingCount > 0 && (
                                    <span
                                        className={`${linkColorClass} ms-1`}
                                        style={{
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap'
                                        }}
                                        onClick={toggleDates}
                                        title="Cliquer pour réduire"
                                    >
                                        (réduire)
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                } else {
                    return 'À la demande';
                }
            } catch (error) {
                console.warn('Erreur de parsing des dates:', error);
                return 'À la demande';
            }
        }

        // Si ce n'est pas une liste de dates mais une chaîne non vide
        if (typeof periode === 'string' && periode.trim() !== '' && periode !== '[]') {
            return (
                <span className={`small text-muted`}>
                    {periode}
                </span>
            );
        }

        return 'À la demande';
    };

    // Récupérer les questions depuis l'API
    const fetchQuestions = async () => {
        try {
            const response = await axios.get(process.env.REACT_APP_HOTE + '/liste-questions-client/');
            if (response.data.success) {
                // Initialiser les réponses
                const questionsWithResponses = response.data.questions.map(q => ({
                    ...q,
                    note: q.type_question === 'echelle' ? null : null,
                    reponse: q.type_question === 'ouverte' ? '' : ''
                }));
                setQuestions(questionsWithResponses);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des questions:', error);
            showAlert('Erreur lors du chargement des questions', 'danger');
            // Utiliser les questions fictives en cas d'erreur
            setQuestions(questionsFictives.filter(q => q.statut === 'actif'));
        }
    };

    // Fonction pour formater le label d'une formation (comme dans ListeSouscriptions)
    const getFormationLabel = (formation) => {
        const baseLabel = formation.nom;
        const periodLabel = formatFormationPeriod(formation.periode);

        // Prix en fonction du type de financement
        const prix = getPrixFormation(formation);
        const priceLabel = prix ? `${parseInt(prix).toLocaleString('fr-FR')} FCFA HT` : '';

        return `${baseLabel} (${periodLabel}) ${priceLabel ? `- ${priceLabel}` : ''}`;
    };

    // Sélectionner une formation
    const handleSelectFormation = (selectedOption) => {
        if (selectedOption) {
            setSelectedFormation(selectedOption.data);
            // Charger les questions après sélection
            fetchQuestions();
        } else {
            setSelectedFormation(null);
        }
    };

    // Passer à l'étape des questions
    const handleNextToQuestions = () => {
        if (!selectedFormation) {
            showAlert('Veuillez sélectionner une formation', 'warning');
            return;
        }
        setStep(2);
    };

    // Retour à la sélection de formation
    const handleBackToFormations = () => {
        setStep(1);
        setCurrentQuestionIndex(0);
    };

    const showAlert = (message, type = 'success') => {
        setAlert({ message, type });
        setTimeout(() => setAlert({ message: '', type: '' }), 5000);
    };

    const fermerNotif = () => {
        setAlert({ message: '', type: '' });
    };

    // Navigation entre questions
    const handleNext = () => {
        const currentQuestion = questions[currentQuestionIndex];

        // Validation avant de passer à la question suivante
        if (currentQuestion.type_question === 'echelle' && currentQuestion.note === null) {
            showAlert('Veuillez attribuer une note avant de continuer', 'warning');
            return;
        }

        if (currentQuestion.type_question === 'ouverte' && (!currentQuestion.reponse || currentQuestion.reponse.trim() === '')) {
            showAlert('Veuillez saisir une réponse avant de continuer', 'warning');
            return;
        }

        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    // Gestion des notes (étoiles)
    const handleStarClick = (rating) => {
        const updatedQuestions = [...questions];
        updatedQuestions[currentQuestionIndex].note = rating;
        setQuestions(updatedQuestions);
    };

    // Gestion des réponses textuelles
    const handleTextResponseChange = (text) => {
        const updatedQuestions = [...questions];
        updatedQuestions[currentQuestionIndex].reponse = text;
        setQuestions(updatedQuestions);
    };

    // Vérifier si toutes les questions sont répondues
    const isAllQuestionsAnswered = () => {
        return questions.every(question => {
            if (question.type_question === 'echelle') {
                return question.note !== null;
            } else {
                return question.reponse && question.reponse.trim().length > 0;
            }
        });
    };

    // Soumettre le formulaire
    const handleSubmit = async () => {
        if (!isAllQuestionsAnswered()) {
            showAlert('Veuillez répondre à toutes les questions avant de soumettre.', 'warning');
            return;
        }

        setIsSubmitting(true);

        try {
            // Préparer les données à envoyer
            const avisData = {
                etapeId: etapeId,
                user_id: user_id,
                formation_reference: selectedFormation.reference,  // Note: changé de formation_id à formation_reference
                formation_nom: selectedFormation.nom,
                questions: questions.map(q => ({
                    question_id: q.id,
                    note: q.type_question === 'echelle' ? q.note : null,
                    reponse: q.type_question === 'ouverte' ? q.reponse : null
                }))
            };

            console.log('Données envoyées:', avisData); // Pour debug

            // Envoyer à l'API
            const response = await axios.post(
                process.env.REACT_APP_HOTE + '/enregistrer-avis-client/',
                avisData
            );

            console.log('Réponse API:', response.data); // Pour debug

            if (response.data.success) {
                setFormSubmitted(true);
                showAlert(response.data.message || 'Votre avis a été enregistré avec succès ! Merci pour votre participation.', 'success');
                const terminerEtapeResponse = await fetch(
                    `${process.env.REACT_APP_HOTE}/terminer-etape-utilisateur/${user_id}/${etapeId}/`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (terminerEtapeResponse.ok) {
                    navigate('/Parcours')
                }
            } else {
                showAlert(response.data.error || 'Erreur lors de l\'enregistrement de l\'avis', 'danger');
            }
        } catch (error) {
            console.error('Erreur lors de l\'envoi:', error);

            // Gestion détaillée des erreurs
            if (error.response) {
                // La requête a été faite et le serveur a répondu avec un code d'erreur
                console.error('Réponse erreur:', error.response.data);
                console.error('Statut:', error.response.status);

                if (error.response.data && error.response.data.error) {
                    showAlert(`Erreur serveur: ${error.response.data.error}`, 'danger');
                } else {
                    showAlert(`Erreur serveur (${error.response.status}): ${error.response.statusText}`, 'danger');
                }
            } else if (error.request) {
                // La requête a été faite mais aucune réponse n'a été reçue
                console.error('Pas de réponse:', error.request);
                showAlert('Le serveur ne répond pas. Veuillez vérifier votre connexion internet.', 'danger');
            } else {
                // Une erreur s'est produite lors de la configuration de la requête
                console.error('Erreur de configuration:', error.message);
                showAlert(`Erreur de configuration: ${error.message}`, 'danger');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Rendu des étoiles
    const renderStars = () => {
        const currentQuestion = questions[currentQuestionIndex];
        const currentRating = currentQuestion.note || 0;

        return (
            <div className="text-center mt-0">
                <div className="stars-display">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            className="star-button btn btn-link p-0"
                            onClick={() => handleStarClick(star)}
                            style={{
                                fontSize: '4rem',
                                color: star <= currentRating ? '#ffc107' : '#e4e5e9',
                                transition: 'color 0.2s',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            {star <= currentRating ? '★' : '☆'}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    // Rendu d'une question ouverte
    const renderTextResponse = () => {
        const currentQuestion = questions[currentQuestionIndex];

        return (
            <div className="text-response-container mb-3">
                <div className="mb-3">
                    <h4>Votre réponse</h4>
                    <p className="text-muted">
                        Veuillez partager vos pensées en toute franchise
                    </p>
                </div>

                <textarea
                    className="form-control"
                    rows={6}
                    value={currentQuestion.reponse || ''}
                    onChange={(e) => handleTextResponseChange(e.target.value)}
                    placeholder="Tapez votre réponse ici..."
                    style={{ fontSize: '16px' }}
                />

                {currentQuestion.reponse && (
                    <div className="mt-2">
                        <small className="text-muted">
                            {currentQuestion.reponse.length} caractères
                        </small>
                    </div>
                )}
            </div>
        );
    };

    // Rendu de la barre de progression
    const renderProgressBar = () => {
        const answeredCount = questions.filter(q =>
            q.type_question === 'echelle' ? q.note !== null : q.reponse && q.reponse.trim() !== ''
        ).length;

        const progressPercentage = (answeredCount / questions.length) * 100;

        return (
            <div className="progress-container mb-4">
                <div className="d-flex justify-content-between mb-2">
                    <small className="text-muted">
                        Question {currentQuestionIndex + 1} sur {questions.length}
                    </small>
                    <small className="text-muted">
                        {answeredCount}/{questions.length} répondues
                    </small>
                </div>
                <div className="progress" style={{ height: '10px' }}>
                    <div
                        className="progress-bar bg-success"
                        role="progressbar"
                        style={{ width: `${progressPercentage}%` }}
                        aria-valuenow={progressPercentage}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    ></div>
                </div>
            </div>
        );
    };

    // Filtrer les formations par recherche
    const filteredFormations = formations.filter(formation =>
        formation.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formation.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formation.lieu.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Options pour le select avec affichage amélioré
    const formationOptions = filteredFormations.map(formation => {
        const prix = getPrixFormation(formation);
        return {
            value: `formation_${formation.id}`,
            label: (
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <div className="font-weight-bold">{formation.nom}</div>
                        <div className="small text-muted">
                            <span className="mr-2">
                                <FaCalendarAlt className="mr-1" />
                                {formatFormationPeriod(formation.periode)}
                            </span>
                            <span className="mr-2">|</span>
                            <span>
                                <strong>{parseInt(prix || 0).toLocaleString('fr-FR')} FCFA HT</strong>
                            </span>
                        </div>
                    </div>
                    {/* <Badge bg="light" text="dark" className="ml-2">
                        {formation.reference}
                    </Badge> */}
                </div>
            ),
            type: 'formation',
            data: formation
        };
    });

    // Écran de chargement
    if (isLoading) {
        return (
            <div id="wrapper">
                <Sidebar user_id={user_id} titre={titre} />
                <div id="page-wrapper" className="gray-bg">
                    <Navbar />
                    <div className="wrapper wrapper-content animated fadeInRight">
                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <div className="text-center py-5">
                                    <div className="spinner-border text-success" role="status"></div>
                                    <p className="mt-3">Chargement...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Étape 1 : Sélection de la formation
    if (step === 1) {
        return (
            <div id="wrapper">
                <Sidebar user_id={user_id} titre={titre} />
                <div id="page-wrapper" className="gray-bg">
                    <Navbar />
                    <div>
                        <div className="row wrapper border-bottom white-bg page-heading">
                            <div className="col-lg-12">
                                <div className="">
                                    <h2 className='d-flex align-items-center'>
                                        <FaStar size={25} className='mr-2 text-warning' />
                                        Évaluation Client
                                    </h2>
                                    <small>Sélectionnez la formation que vous avez suivie</small>
                                </div>
                                <small id='hbreadcrumb' className='d-flex justify-content-end'>
                                    <ol className="breadcrumb custom-breadcrumb">
                                        <li className="breadcrumb-item custom-breadcrumb-item">
                                            <Link to={`/`}> Accueil</Link>
                                        </li>
                                        <li className="breadcrumb-item custom-breadcrumb-item">
                                            <Link to={`/avis-client`}> Évaluation</Link>
                                        </li>
                                    </ol>
                                </small>
                            </div>
                        </div>

                        {alert.message && (
                            <div className={`alert alert-${alert.type} row justify-content-between position-relative m-2 mt-2`}>
                                <div>{alert.message}</div>
                                <span
                                    onClick={fermerNotif}
                                    style={{
                                        position: 'absolute',
                                        top: '0px',
                                        right: '10px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <i className='fa fa-close'></i>
                                </span>
                            </div>
                        )}

                        <div className="mt-2">
                            <div className="wrapper wrapper-content animated fadeInRight">
                                <div className="row justify-content-center">
                                    <div className="col-lg-10">
                                        <Card className="shadow-sm border-0">
                                            <Card.Header className="bg-success text-white">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h4 className="mb-0">
                                                            <FaChalkboardTeacher className="mr-2" />
                                                            Sélectionnez votre formation
                                                        </h4>
                                                        <small>Choisissez une formation pour donner votre avis</small>
                                                    </div>
                                                    <span className="badge bg-light text-dark">
                                                        {formations.length} formation{formations.length > 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </Card.Header>

                                            <Card.Body className="py-4">
                                                {loadingFormations ? (
                                                    <div className="text-center py-5">
                                                        <div className="spinner-border text-success" role="status"></div>
                                                        <p className="mt-3">Chargement des formations...</p>
                                                    </div>
                                                ) : formations.length === 0 ? (
                                                    <div className="text-center py-5">
                                                        <FaChalkboardTeacher size={50} className="text-muted mb-3" />
                                                        <h5>Aucune formation disponible</h5>
                                                        <p className="text-muted">
                                                            Aucune formation n'est actuellement disponible pour évaluation.
                                                        </p>
                                                        <Link to="/" className="btn btn-success mt-3">
                                                            Retour à l'accueil
                                                        </Link>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <Alert variant="info" className="mb-4">
                                                            <strong>Instructions :</strong> Veuillez sélectionner la formation pour laquelle vous souhaitez donner votre avis.
                                                        </Alert>

                                                        <Form.Group className="mb-4">
                                                            <Form.Label>
                                                                <strong>Rechercher et sélectionner une formation</strong>
                                                            </Form.Label>
                                                            {/* <div className="input-group mb-3">
                                                                <div className="input-group-prepend">
                                                                    <span className="input-group-text">
                                                                        <FaSearch />
                                                                    </span>
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Rechercher une formation par nom, référence ou lieu..."
                                                                    value={searchTerm}
                                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                                />
                                                            </div> */}

                                                            <Select
                                                                options={formationOptions}
                                                                value={
                                                                    selectedFormation ? {
                                                                        value: `formation_${selectedFormation.reference}`,
                                                                        label: getFormationLabel(selectedFormation),
                                                                        type: 'formation',
                                                                        data: selectedFormation
                                                                    } : null
                                                                }
                                                                onChange={handleSelectFormation}
                                                                placeholder="Sélectionnez une formation dans la liste..."
                                                                isSearchable
                                                                isClearable
                                                                className="basic-select"
                                                                classNamePrefix="select"
                                                                formatOptionLabel={({ label }) => label}
                                                                styles={{
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        border: '1px solid #dee2e6',
                                                                        borderRadius: '0.375rem',
                                                                        minHeight: '45px',
                                                                        '&:hover': {
                                                                            borderColor: '#326dc4ff'
                                                                        }
                                                                    }),
                                                                    option: (base, state) => ({
                                                                        ...base,
                                                                        backgroundColor: state.isSelected ? '#326dc4ff' : state.isFocused ? '#e9f5ee' : 'white',
                                                                        color: state.isSelected ? 'white' : '#212529',
                                                                        padding: '10px 15px',
                                                                        '&:hover': {
                                                                            backgroundColor: '#e9f5ee'
                                                                        }
                                                                    }),
                                                                    singleValue: (base) => ({
                                                                        ...base,
                                                                        display: 'flex',
                                                                        alignItems: 'center'
                                                                    })
                                                                }}
                                                            />

                                                            <Form.Text className="text-muted">
                                                                {filteredFormations.length} formation{filteredFormations.length > 1 ? 's' : ''} trouvée{filteredFormations.length > 1 ? 's' : ''}
                                                            </Form.Text>
                                                        </Form.Group>

                                                        {/* {selectedFormation && (
                                                            <Card className="border-success mb-4">
                                                                <Card.Header className="bg-success text-white">
                                                                    <h6 className="mb-0">Formation sélectionnée</h6>
                                                                </Card.Header>
                                                                <Card.Body>
                                                                    <div className="d-flex">
                                                                        {selectedFormation.photo_url && (
                                                                            <img 
                                                                                src={`${process.env.REACT_APP_HOTE}${selectedFormation.photo_url}`}
                                                                                alt={selectedFormation.nom}
                                                                                style={{
                                                                                    width: '100px',
                                                                                    height: '100px',
                                                                                    objectFit: 'cover',
                                                                                    borderRadius: '8px',
                                                                                    marginRight: '15px'
                                                                                }}
                                                                            />
                                                                        )}
                                                                        <div className="flex-grow-1">
                                                                            <h5>{selectedFormation.nom}</h5>
                                                                            <div className="row">
                                                                                <div className="col-md-6">
                                                                                    <small className="text-muted d-block mb-1">
                                                                                        <strong>Référence:</strong> {selectedFormation.reference}
                                                                                    </small>
                                                                                    <small className="text-muted d-block mb-1">
                                                                                        <strong>Lieu:</strong> {selectedFormation.lieu}
                                                                                    </small>
                                                                                    <small className="text-muted d-block mb-1">
                                                                                        <strong>Mode:</strong> {selectedFormation.option === 'en_ligne' ? 'Virtuel' : 
                                                                                                                selectedFormation.option === 'presentiel' ? 'Présentiel' : 'Les deux'}
                                                                                    </small>
                                                                                </div>
                                                                                <div className="col-md-6">
                                                                                    <small className="text-muted d-block mb-1">
                                                                                        <strong>Prix:</strong> {parseInt(getPrixFormation(selectedFormation) || 0).toLocaleString('fr-FR')} FCFA HT
                                                                                    </small>
                                                                                    <small className="text-muted d-block mb-1">
                                                                                        <strong>Période:</strong> 
                                                                                    </small>
                                                                                    <div className="formation-dates-list mb-1">
                                                                                        {getAllDatesDisplay(selectedFormation.periode, selectedFormation.id)}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Card.Body>
                                                            </Card>
                                                        )} */}
                                                    </>
                                                )}
                                            </Card.Body>

                                            <Card.Footer className="bg-light">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        {selectedFormation ? (
                                                            <div className="d-flex align-items-center text-success">
                                                                <FaCheck className="me-2" />
                                                                <span className="font-weight-bold">
                                                                    {selectedFormation.nom}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">
                                                                Aucune formation sélectionnée
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="d-flex gap-2">
                                                        <Link to="/Parcours" className="btn btn-outline-secondary mr-2">
                                                            Retour
                                                        </Link>

                                                        <Button
                                                            variant="success"
                                                            onClick={handleNextToQuestions}
                                                            disabled={!selectedFormation}
                                                            className="d-flex align-items-center"
                                                        >
                                                            Continuer vers l'évaluation
                                                            <FaArrowRight className="ml-2" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card.Footer>
                                        </Card>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Étape 2 : Réponse aux questions
    if (step === 2 && questions.length > 0) {
        const currentQuestion = questions[currentQuestionIndex];

        return (
            <div id="wrapper">
                <Sidebar user_id={user_id} titre={titre} />
                <div id="page-wrapper" className="gray-bg">
                    <Navbar />
                    <div>
                        <div className="row wrapper border-bottom white-bg page-heading">
                            <div className="col-lg-12">
                                <div className="">
                                    <h2 className='d-flex align-items-center'>
                                        <FaStar size={25} className='mr-2 text-warning' />
                                        Évaluation : {selectedFormation.nom}
                                    </h2>
                                    <small>Partagez votre expérience concernant cette formation</small>
                                </div>
                                <small id='hbreadcrumb' className='d-flex justify-content-end'>
                                    <ol className="breadcrumb custom-breadcrumb">
                                        <li className="breadcrumb-item custom-breadcrumb-item">
                                            <Link to={`/`}> Accueil</Link>
                                        </li>
                                        <li className="breadcrumb-item custom-breadcrumb-item">
                                            <Link to={`/avis-client`}> Évaluation</Link>
                                        </li>
                                        <li className="breadcrumb-item custom-breadcrumb-item active">
                                            {selectedFormation.nom}
                                        </li>
                                    </ol>
                                </small>
                            </div>
                        </div>

                        {alert.message && (
                            <div className={`alert alert-${alert.type} row justify-content-between position-relative m-2 mt-2`}>
                                <div>{alert.message}</div>
                                <span
                                    onClick={fermerNotif}
                                    style={{
                                        position: 'absolute',
                                        top: '0px',
                                        right: '10px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <i className='fa fa-close'></i>
                                </span>
                            </div>
                        )}

                        <div className="mt-2">
                            <div className="wrapper wrapper-content animated fadeInRight">
                                <div className="row justify-content-center">
                                    <div className="col-lg-8">
                                        <Card className="shadow-sm border-0">
                                            <Card.Header className="bg-success text-white">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h4 className="mb-0">
                                                            <FaStar className="mr-2" />
                                                            Votre Avis - {selectedFormation.nom}
                                                        </h4>
                                                        <small>Question par question</small>
                                                    </div>
                                                    <span className="badge bg-light text-dark">
                                                        {currentQuestion.reference}
                                                    </span>
                                                </div>
                                            </Card.Header>

                                            <Card.Body className="py-4">
                                                {renderProgressBar()}

                                                <div className="question-container">
                                                    <div className="question-header">
                                                        <h3 className="question-text">
                                                            {currentQuestion.question}
                                                        </h3>
                                                    </div>

                                                    <div className="response-container">
                                                        {currentQuestion.type_question === 'echelle' ? (
                                                            renderStars()
                                                        ) : (
                                                            renderTextResponse()
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="navigation-buttons">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <Button
                                                            variant="outline-secondary"
                                                            onClick={handleBackToFormations}
                                                            className="d-flex align-items-center"
                                                        >
                                                            <FaArrowLeft className="mr-2" />
                                                            Changer de formation
                                                        </Button>

                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                variant="outline-secondary"
                                                                onClick={handlePrevious}
                                                                disabled={currentQuestionIndex === 0}
                                                                className="d-flex align-items-center mr-2"
                                                            >
                                                                <FaArrowLeft className="mr-2" />
                                                                Précédent
                                                            </Button>

                                                            {currentQuestionIndex === questions.length - 1 ? (
                                                                <Button
                                                                    variant="success"
                                                                    onClick={handleSubmit}
                                                                    disabled={isSubmitting}
                                                                    className="d-flex align-items-center"
                                                                >
                                                                    {isSubmitting ? (
                                                                        <>
                                                                            <span className="spinner-border spinner-border-sm mr-2"></span>
                                                                            Envoi...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FaPaperPlane className="mr-2" />
                                                                            Soumettre l'avis
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    variant="success"
                                                                    onClick={handleNext}
                                                                    className="d-flex align-items-center"
                                                                >
                                                                    Suivant
                                                                    <FaArrowRight className="ml-2" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card.Body>

                                            <Card.Footer className="bg-light">
                                                <div className="row">
                                                    <div className="col-md-8">
                                                        <small className="text-muted">
                                                            <i className="fa fa-info-circle mr-1"></i>
                                                            {/* Formation : <strong>{selectedFormation.nom}</strong> -  */}
                                                            Votre avis est anonyme et confidentiel.
                                                            Toutes les réponses sont obligatoires.
                                                        </small>
                                                    </div>
                                                    <div className="col-md-4 text-right">
                                                        <small className="text-muted">
                                                            {currentQuestionIndex + 1} / {questions.length}
                                                        </small>
                                                    </div>
                                                </div>
                                            </Card.Footer>
                                        </Card>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Écran de confirmation après soumission
    if (formSubmitted) {
        return (
            <div id="wrapper">
                <Sidebar user_id={user_id} titre={titre} />
                <div id="page-wrapper" className="gray-bg">
                    <Navbar />
                    <div className="wrapper wrapper-content animated fadeInRight">
                        <div className="row justify-content-center">
                            <div className="col-lg-6">
                                <Card className="shadow-sm border-0 text-center">
                                    <Card.Body className="py-5">
                                        <div className="mb-4">
                                            <FaCheck className="text-success" size={80} />
                                        </div>
                                        <h2 className="text-success mb-3">Merci !</h2>
                                        <p className="lead mb-4">
                                            Votre avis pour la formation <strong>{selectedFormation?.nom}</strong> a été enregistré avec succès.
                                        </p>
                                        <p className="text-muted mb-4">
                                            Vos commentaires sont précieux pour nous aider à améliorer
                                            continuellement nos formations.
                                        </p>
                                        <div className="d-flex justify-content-center gap-3">
                                            <Link to="/" className="btn btn-success">
                                                Retour à l'accueil
                                            </Link>
                                            <Button
                                                variant="outline-success"
                                                onClick={() => {
                                                    setStep(1);
                                                    setSelectedFormation(null);
                                                    setQuestions([]);
                                                    setCurrentQuestionIndex(0);
                                                    setFormSubmitted(false);
                                                    setSearchTerm('');
                                                    setExpandedDates({});
                                                }}
                                            >
                                                Évaluer une autre formation
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Données fictives pour les questions (fallback)
    const questionsFictives = [
        {
            id: 1,
            reference: "CLI-001",
            question: "Dans l'ensemble, quel est votre degré de satisfaction concernant le cours ?",
            type_question: "echelle",
            statut: "actif",
            note: null
        },
        {
            id: 2,
            reference: "CLI-002",
            question: "Dans quelle mesure le cours a-t-il répondu à vos attentes ?",
            type_question: "echelle",
            statut: "actif",
            note: null
        },
        {
            id: 3,
            reference: "CLI-003",
            question: "Si le contenu de la formation n'a pas répondu à vos attentes, comment pouvons-nous l'améliorer afin qu'il corresponde à vos besoins ?",
            type_question: "ouverte",
            statut: "actif",
            reponse: ""
        },
        {
            id: 4,
            reference: "CLI-004",
            question: "À quel point estimez-vous être confiant afin de mettre habilement en pratique les compétences acquises dans ce cours ?",
            type_question: "echelle",
            statut: "actif",
            note: null
        },
        {
            id: 5,
            reference: "CLI-005",
            question: "Quelle est votre évaluation de la qualité du contenu ?",
            type_question: "echelle",
            statut: "actif",
            note: null
        },
        {
            id: 6,
            reference: "CLI-006",
            question: "Veuillez indiquer votre niveau d'accord avec l'énoncé suivant : La structure et le déroulement du cours étaient logiques.",
            type_question: "echelle",
            statut: "actif",
            note: null
        },
        {
            id: 7,
            reference: "CLI-007",
            question: "Quel niveau de connaissances le formateur avait-il du contenu de la formation ?",
            type_question: "echelle",
            statut: "actif",
            note: null
        },
        {
            id: 8,
            reference: "CLI-008",
            question: "Si vous aviez choisi les options 'Connaissances moyennes' ou 'Aucune connaissance' veuillez nous en donner les raisons.",
            type_question: "ouverte",
            statut: "actif",
            reponse: ""
        },
        {
            id: 9,
            reference: "CLI-009",
            question: "Dans l'ensemble, comment évaluez-vous le formateur ?",
            type_question: "echelle",
            statut: "actif",
            note: null
        },
        {
            id: 10,
            reference: "CLI-010",
            question: "Sur une échelle de 0 à 5, quelle est la probabilité que vous recommandiez notre entreprise à un ami ou à un collègue ?",
            type_question: "echelle",
            statut: "actif",
            note: null
        }
    ];

    return null;
};

export default FormulaireAvisClient;